/**
 * Social Media URL Validation and Username Extraction Utility
 * 
 * This utility provides functions to validate social media URLs and extract usernames
 * from various social media platforms including Instagram, YouTube, Facebook, TikTok, and Discord.
 */

interface SocialMediaPattern {
  name: string;
  urlPatterns: RegExp[];
  usernameExtractor: (url: string) => string | null;
}

const socialMediaPatterns: SocialMediaPattern[] = [
  {
    name: 'instagram',
    urlPatterns: [
      /https?:\/\/(www\.)?instagram\.com\/([^/?#]+)/i
    ],
    usernameExtractor: (url: string) => {
      const match = url.match(/instagram\.com\/([^/?#]+)/i);
      return match ? match[1] : null;
    }
  },
  {
    name: 'youtube',
    urlPatterns: [
      /https?:\/\/(www\.)?youtube\.com\/(user\/|c\/|@)?([^/?#]+)/i,
      /https?:\/\/(www\.)?youtube\.com\/channel\/([^/?#]+)/i
    ],
    usernameExtractor: (url: string) => {
      // For user/channel format
      let match = url.match(/youtube\.com\/channel\/([^/?#]+)/i);
      if (match) return match[1];
      
      // For user/c/@username format
      match = url.match(/youtube\.com\/(user\/|c\/|@)?([^/?#]+)/i);
      return match ? match[2] : null;
    }
  },
  {
    name: 'facebook',
    urlPatterns: [
      /https?:\/\/(www\.|web\.)?facebook\.com\/([^/?#]+)/i
    ],
    usernameExtractor: (url: string) => {
      const match = url.match(/facebook\.com\/([^/?#]+)/i);
      return match ? match[1] : null;
    }
  },
  {
    name: 'tiktok',
    urlPatterns: [
      /https?:\/\/(www\.)?tiktok\.com\/@?([^/?#]+)/i
    ],
    usernameExtractor: (url: string) => {
      const match = url.match(/tiktok\.com\/@?([^/?#]+)/i);
      return match ? match[1] : null;
    }
  },
  {
    name: 'discord',
    urlPatterns: [
      /https?:\/\/(www\.)?discord\.(?:gg|com\/invite)\/([a-zA-Z0-9-]+)/i,
      /https?:\/\/(www\.)?discord\.me\/([^/?#]+)/i
    ],
    usernameExtractor: (url: string) => {
      // For discord URLs, return the full URL instead of just the code
      return url.startsWith('http') ? url : `https://${url}`;
    }
  }
];

/**
 * Checks if a string is a valid URL
 * @param input - String to check
 * @returns boolean indicating if input is a URL
 */
export const isUrl = (input: string): boolean => {
  if (!input) return false;
  
  try {
    // Check if it has common URL indicators
    if (input.includes('http://') || input.includes('https://') || 
        input.includes('www.') || /\.[a-z]{2,}(\/|$)/i.test(input)) {
      
      // Try to construct a URL object as final validation
      new URL(input.startsWith('http') ? input : `https://${input}`);
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Identifies which social media platform a URL belongs to
 * @param url - The URL to check
 * @returns The name of the social media platform or null if not recognized
 */
export const identifySocialMediaPlatform = (url: string): string | null => {
  if (!isUrl(url)) return null;
  
  // Normalize URL by ensuring it has a protocol
  const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
  
  for (const platform of socialMediaPatterns) {
    for (const pattern of platform.urlPatterns) {
      if (pattern.test(normalizedUrl)) {
        return platform.name;
      }
    }
  }
  
  return null;
};

/**
 * Extracts username from a social media URL
 * @param input - URL or username string
 * @returns Extracted username or the original input if not a recognized URL
 */
export const extractSocialMediaUsername = (input: string): string => {
  if (!input) return '';
  
  // If it's not a URL, assume it's already a username
  if (!isUrl(input)) return input;
  
  // Normalize URL by ensuring it has a protocol
  const normalizedUrl = input.startsWith('http') ? input : `https://${input}`;
  
  // Try to extract using platform-specific extractors
  for (const platform of socialMediaPatterns) {
    for (const pattern of platform.urlPatterns) {
      if (pattern.test(normalizedUrl)) {
        const username = platform.usernameExtractor(normalizedUrl);
        if (username) return username;
      }
    }
  }
  
  // Fallback: try to extract the last path segment
  try {
    const url = new URL(normalizedUrl);
    const pathParts = url.pathname.split('/').filter(Boolean);
    return pathParts.length > 0 ? pathParts[pathParts.length - 1] : input;
  } catch (e) {
    // If URL parsing fails, return the original input
    return input;
  }
};

/**
 * Validates if a string is a valid social media username or URL
 * @param input - String to validate
 * @param platform - Optional platform to validate against (instagram, youtube, facebook, tiktok)
 * @returns boolean indicating if input is valid
 */
export const validateSocialMedia = (input: string, platform?: string): boolean => {
  if (!input) return false;
  
  // If it's a URL, validate it's for the correct platform
  if (isUrl(input)) {
    const detectedPlatform = identifySocialMediaPlatform(input);
    
    // If platform is specified, check if it matches
    if (platform && detectedPlatform) {
      return detectedPlatform.toLowerCase() === platform.toLowerCase();
    }
    
    // Otherwise just check if it's any recognized social media
    return detectedPlatform !== null;
  }
  
  // If it's not a URL, assume it's a username and do basic validation
  // Username validation rules vary by platform, these are general rules
  return /^[a-zA-Z0-9._-]{2,30}$/.test(input);
};

/**
 * Validates social media input and throws descriptive error if invalid
 * @param input - String to validate (username or URL)
 * @param platform - Platform name (instagram, youtube, facebook, tiktok, discord, other)
 * @returns object with { isValid: boolean, error?: string }
 */
export const validateSocialMediaInput = (
  input: string, 
  platform: string
): { isValid: boolean; error?: string } => {
  if (!input) {
    return { isValid: true }; // Empty input is valid (user can leave it blank)
  }

  // Allow Discord and Other platforms to have any URL
  if (platform.toUpperCase() === 'DISCORD' || platform.toUpperCase() === 'OTHER') {
    // For Discord, just ensure URLs start with http
    if (platform.toUpperCase() === 'DISCORD' && isUrl(input) && !input.startsWith('http')) {
      return {
        isValid: false,
        error: 'Discord URL must start with http:// or https://'
      };
    }
    return { isValid: true };
  }

  // Check if input is a URL
  if (isUrl(input)) {
    const detectedPlatform = identifySocialMediaPlatform(input);
    
    // If we detected a platform, check if it matches
    if (detectedPlatform) {
      if (detectedPlatform.toLowerCase() !== platform.toLowerCase()) {
        return {
          isValid: false,
          error: `This appears to be a ${detectedPlatform} URL, but you're entering it for ${platform}. Please use the correct platform field or enter just the username.`
        };
      }
      // URL is for the correct platform
      return { isValid: true };
    } else {
      // URL detected but platform not recognized
      return {
        isValid: false,
        error: `This URL doesn't appear to be a valid ${platform} link. Please enter just your ${platform} username or a valid ${platform} URL.`
      };
    }
  }

  // Not a URL, assume it's a username - basic validation
  // Allow more flexible username formats since different platforms have different rules
  if (input.length < 2) {
    return {
      isValid: false,
      error: 'Username must be at least 2 characters long'
    };
  }

  return { isValid: true };
};

/**
 * Formats a social media URL from a username
 * @param username - The username to format (or full URL if already provided)
 * @param platform - The platform (instagram, youtube, facebook, tiktok)
 * @returns Formatted URL or null if platform is not supported
 */
export const formatSocialMediaUrl = (username: string, platform: string): string | undefined => {
  if (!username) return undefined;
  
  // If username is already a complete URL, return it as-is
  if (isUrl(username) && username.startsWith('http')) {
    return username;
  }
  
  // First, clean the username by removing @ if present
  const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
  
  // Convert platform to lowercase for case-insensitive comparison
  const platformLower = platform.toLowerCase();
  
  switch (platformLower) {
    case 'instagram':
      return `https://www.instagram.com/${cleanUsername}`;
    case 'youtube':
      // YouTube URLs should have @ before the username
      return `https://www.youtube.com/@${cleanUsername}`;
    case 'facebook':
      return `https://www.facebook.com/${cleanUsername}`;
    case 'tiktok':
      // TikTok URLs should have @ before the username
      return `https://www.tiktok.com/@${cleanUsername}`;
    case 'discord':
      // For Discord, if it's already a URL, return it as is, otherwise format as invite link
      if (cleanUsername.startsWith('http')) {
        return cleanUsername;
      }
      return `https://discord.gg/${cleanUsername}`;
    case 'other':
      // For "OTHER" platform, if it looks like a URL, ensure it has protocol
      if (cleanUsername.includes('.')) {
        return cleanUsername.startsWith('http') ? cleanUsername : `https://${cleanUsername}`;
      }
      return cleanUsername;
    default:
      // Handle uppercase platform names
      if (platform === 'INSTAGRAM') {
        return `https://www.instagram.com/${cleanUsername}`;
      } else if (platform === 'YOUTUBE') {
        return `https://www.youtube.com/@${cleanUsername}`;
      } else if (platform === 'FACEBOOK') {
        return `https://www.facebook.com/${cleanUsername}`;
      } else if (platform === 'TIKTOK') {
        return `https://www.tiktok.com/@${cleanUsername}`;
      } else if (platform === 'DISCORD') {
        // For Discord, if it's already a URL, return it as is, otherwise format as invite link
        if (cleanUsername.startsWith('http')) {
          return cleanUsername;
        }
        return `https://discord.gg/${cleanUsername}`;
      } else if (platform === 'OTHER') {
        // For "OTHER" platform, if it looks like a URL, ensure it has protocol
        if (cleanUsername.includes('.')) {
          return cleanUsername.startsWith('http') ? cleanUsername : `https://${cleanUsername}`;
        }
        return cleanUsername;
      }
      return undefined;
  }
};
